const slugify = (value: string): string => {
  if (!value) return '';

  const normalized = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // remove diacritics
    .toLowerCase();

  const slug = normalized.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  return slug || normalized.replace(/\s+/g, '-');
};

const TEMPLATE_DOMAIN_PREFIX = 'template.';

/** Convert leftover RACE JS (`${keywords.join("%20")}`) into `{keywords}`. */
export const normalizeRaceSearchTemplate = (template: string): string => {
  if (typeof template !== 'string' || !template.includes('${')) {
    return template;
  }
  return template
    .replace(/\$\{(?:keywords|query)\.join\(\s*['"](?:%20| |\+)['"]\s*\)\}/gi, '{keywords}')
    .replace(/\$\{domain\}/gi, '{domain}')
    .replace(/\$\{(?:keywords|query)\}/gi, '{keywords}');
};

/** Host for `{domain}` substitution. Strips a `template.` prefix, keeps www. */
export const liveSearchDomain = (value: string): string => {
  let host = (value || '').trim();
  if (!host) return '';
  if (host.includes('://')) {
    try {
      host = new URL(host).hostname || host;
    } catch {
      host = host.split('://')[1]?.split('/')[0] || host;
    }
  } else {
    host = host.split('/')[0];
  }
  host = host.toLowerCase();
  if (host.startsWith(TEMPLATE_DOMAIN_PREFIX)) {
    host = host.slice(TEMPLATE_DOMAIN_PREFIX.length);
  }
  return host;
};

export interface KeywordPlaceholderOptions {
  keyword?: string;
}

export const applyKeywordPlaceholders = (template: string, keyword: string): string => {
  if (typeof template !== 'string') {
    return template;
  }

  const normalized = normalizeRaceSearchTemplate(template);
  if (!normalized.includes('{')) {
    return normalized;
  }

  const raw = (keyword ?? '').trim();
  const encoded = encodeURIComponent(raw);
  const plus = raw ? raw.trim().split(/\s+/).join('+') : '';
  const slug = slugify(raw);

  const replacements: Record<string, string> = {
    '{keyword}': encoded,
    '{keywords}': encoded,
    '{keyword_encoded}': encoded,
    '{keywords_encoded}': encoded,
    '{keyword_plus}': plus,
    '{keywords_plus}': plus,
    '{keyword_raw}': raw,
    '{keywords_raw}': raw,
    '{keyword_slug}': slug,
    '{keywords_slug}': slug,
  };

  let result = normalized;
  for (const [token, value] of Object.entries(replacements)) {
    if (result.includes(token)) {
      result = result.split(token).join(value);
    }
  }

  return result;
};

export type SearchUrlDefinition = {
  config?: Record<string, unknown> | null;
  domain?: string | null;
  marketplace?: string | null;
  name?: string | null;
};

function templateFromConfig(definition?: SearchUrlDefinition | null): string {
  const raw = definition?.config?.search_url_template;
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  return trimmed ? normalizeRaceSearchTemplate(trimmed) : '';
}

function hostFromBaseUrls(definition?: SearchUrlDefinition | null): string {
  const baseUrls = definition?.config?.base_urls;
  if (!Array.isArray(baseUrls)) return '';
  for (const entry of baseUrls) {
    if (typeof entry !== 'string' || !entry.trim()) continue;
    const host = liveSearchDomain(entry);
    if (host) return host;
  }
  return '';
}

/**
 * Effective search URL template for a marketplace definition.
 * List payloads omit `config` (and locale children inherit the template from
 * the parent), so callers should pass a resolved definition when possible.
 */
export const resolveSearchUrlTemplate = (definition?: SearchUrlDefinition | null): string => {
  const fromConfig = templateFromConfig(definition);
  if (fromConfig) return fromConfig;

  const domain = liveSearchDomain(definition?.domain || '');
  const marketplace = String(definition?.marketplace || definition?.name || '').toLowerCase();
  if (marketplace.includes('amazon')) {
    return `https://${domain || 'www.amazon.com'}/s?k={query}`;
  }
  if (marketplace.includes('ebay')) {
    return `https://${domain || 'www.ebay.com'}/sch/i.html?_nkw={query}`;
  }
  if (marketplace.includes('alibaba')) {
    return `https://${domain || 'www.alibaba.com'}/trade/search?SearchText={query}`;
  }
  if (marketplace.includes('shopee')) {
    return `https://${domain || 'shopee.com'}/search?keyword={keywords_slug}`;
  }
  return `https://${domain || 'example.com'}/search?q={query}`;
};

export const domainForSearchTemplate = (template: string, domain: string): string => {
  const d = (domain || '').trim();
  if (!d) return '';
  // listado.{domain} / www.{domain}: never keep a www. prefix on the registrable host
  if (/\.\{domain\}/i.test(template)) {
    return d.replace(/^www\./i, '');
  }
  return d;
};

export const generateSearchUrlsForMarketplace = (
  definition: SearchUrlDefinition | null | undefined,
  keywords: string
): string[] => {
  const template = resolveSearchUrlTemplate(definition);
  const configDomain =
    typeof definition?.config?.domain === 'string' ? definition.config.domain : '';
  const rawDomain =
    liveSearchDomain(definition?.domain || configDomain || '') || hostFromBaseUrls(definition);
  const domain = domainForSearchTemplate(template, rawDomain);
  return keywords
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean)
    .map(kw => {
      let url = template;
      if (url.includes('{domain}') && domain) {
        url = url.split('{domain}').join(domain);
      }
      if (url.includes('{query}')) {
        url = url.split('{query}').join(encodeURIComponent(kw));
      }
      return applyKeywordPlaceholders(url, kw);
    });
};

/**
 * Infer the query parameter name from a search URL template (e.g. "k" from "?k={query}", "_nkw" from "_nkw={query}").
 */
function getQueryParamFromTemplate(template: string): string | null {
  if (!template || typeof template !== 'string') return null;
  // Match [?&]paramName={query|keyword|keywords...}
  const match = template.match(
    /[?&]([^=]+)=\{(?:query|keyword|keywords)(?:_encoded|_plus|_raw|_slug)?\}/i
  );
  return match ? match[1].trim() : null;
}

/**
 * Extract search keywords from a list of URLs using the marketplace's search_url_template.
 * Used when editing a keyword task: the task stores expanded URLs but we want to show the original keywords in the form.
 * Returns newline-separated keywords (one per URL, deduplicated by value, order preserved).
 */
export function extractKeywordsFromUrls(urls: string[], searchUrlTemplate: string): string {
  if (!urls?.length) return '';
  const paramName = getQueryParamFromTemplate(searchUrlTemplate);
  const seen = new Set<string>();
  const keywords: string[] = [];

  for (const url of urls) {
    try {
      const parsed = new URL(url);
      let value = paramName
        ? parsed.searchParams.get(paramName)
        : (parsed.searchParams.get('q') ??
          parsed.searchParams.get('keyword') ??
          parsed.searchParams.get('k') ??
          parsed.searchParams.get('_nkw') ??
          parsed.searchParams.get('SearchText'));
      if (
        (value == null || !String(value).trim()) &&
        /\{(?:query|keyword|keywords)/i.test(searchUrlTemplate || '')
      ) {
        const parts = parsed.pathname.split('/').filter(Boolean);
        value = parts.length ? parts[parts.length - 1] : null;
      }
      if (value != null && value.trim() !== '') {
        const decoded = decodeURIComponent(value.trim()).replace(/\+/g, ' ');
        if (!seen.has(decoded)) {
          seen.add(decoded);
          keywords.push(decoded);
        }
      }
    } catch {
      // Skip malformed URLs
    }
  }

  return keywords.join('\n');
}
